import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { color as colors } from '@strava/ui-tokens/js';
import { useMap } from './useMap';
import { useMapStyle } from './useMapStyle';

function Fog({
  color,
  range,
  horizonBlend,
  highColor,
  spaceColor,
  starIntensity
}) {
  const map = useMap();
  const mapStyle = useMapStyle();

  useEffect(() => {
    // make sure map has loaded
    if (map.style?._loaded) {
      map.setFog({
        color,
        range,
        'horizon-blend': horizonBlend,
        'high-color': highColor,
        'space-color': spaceColor,
        'star-intensity': starIntensity
      });
    }
  }, [
    map,
    mapStyle,
    color,
    range,
    horizonBlend,
    highColor,
    spaceColor,
    starIntensity
  ]);

  useEffect(() => {
    return () => {
      if (map && map.style) {
        map.setFog(null);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/**
 * For more detail on available options look at the docs
 * @url https://docs.mapbox.com/mapbox-gl-js/style-spec/fog/
 */

Fog.defaultProps = {
  color: colors.colorCoreWhite,
  horizonBlend: 0.5,
  range: [4, 18],
  highColor: '#87ceeb',
  spaceColor: '#d8f2ff',
  starIntensity: 0
};

Fog.propTypes = {
  /**
   * Optional color. Supports interpolate expressions:
   * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
   *
   * The color of the atmosphere region immediately below the horizon and within the
   * `range` and above the horizon and within `horizon-blend`. Using opacity is
   * recommended only for smoothly transitioning fog on/off as anything less than 100%
   * opacity results in more tiles loaded and drawn.
   */
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  /**
   * Optional number between `0` and `1` inclusive. Supports interpolate expressions:
   * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
   *
   * Horizon blend applies a smooth fade from the color of the atmosphere to the color of
   * space. A value of zero leaves a sharp transition from atmosphere to space. Increasing
   * the value blends the color of atmosphere into increasingly high angles of the sky.
   */
  horizonBlend: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),

  /**
   * Optional array of numbers between `-20` and `20` inclusive.
   *
   * The start and end distance range in which fog fades from fully transparent to fully
   * opaque. The distance to the point at the center of the map is defined as zero, so
   * that negative range values are closer to the camera, and positive values are farther
   * away.
   */
  range(props, propName, componentName) {
    if (
      !Array.isArray(props[propName]) ||
      props[propName].length !== 2 ||
      !props[propName].every(
        (v) => typeof v === 'number' && v >= -20 && v <= 20
      )
    ) {
      return new Error(
        `Invalid prop  \`${propName}\` supplied to \`${componentName}\`, expected an array of two numbers between \`-20\` and \`20\`, but got \`${props[propName]}\`.`
      );
    }

    return null;
  },

  /**
   * Optional color. Supports interpolate expressions:
   * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
   *
   * The color of the atmosphere region above the horizon, `high-color` extends further
   * above the horizon than the `color` property and its spread can be controlled with
   * `horizon-blend`. The opacity can be set to `0` to remove the high atmosphere color
   * contribution.
   */
  highColor: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  /**
   * Optional color. Supports interpolate expressions:
   * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
   *
   * The color of the region above the horizon and after the end of the `horizon-blend`
   * contribution. The opacity can be set to `0` to have a transparent background.
   */
  spaceColor: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  /**
   * Optional number between `0` and `1` inclusive. Supports interpolate expressions:
   * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#interpolate
   *
   * A value controlling the star intensity where `0` will show no stars and `1` will show
   * stars at their maximum intensity.
   */
  starIntensity: PropTypes.oneOfType([PropTypes.number, PropTypes.array])
};

export default Fog;
